export default class Chatbot {
  constructor({ apiBaseUrl, onEvent } = {}) {
    this.options = { apiBaseUrl, onEvent };
    this.form = document.querySelector('#chatbot-form');
    this.input = document.querySelector('#chatbot-input');
    this.messages = document.querySelector('#chatbot-messages');
    this.typing = document.querySelector('#chatbot-typing');
    this.sendButton = document.querySelector('#chatbot-send');
    this.resetButton = document.querySelector('#chatbot-reset');
    this.isBackendReady = false;
    this.defaultTypingHTML = this.typing ? this.typing.innerHTML : '';
    this.conversationId = null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.warmupBackend();
  }

  bindEvents() {
    this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
    this.resetButton?.addEventListener('click', () => this.resetConversation());
    this.observeSection();
  }

  resetConversation() {
    const conversationId = this.conversationId;
    this.conversationId = null;
    if (this.messages) this.messages.innerHTML = '';
    this.options.onEvent?.('chat_reset', { conversation_id: conversationId });
    if (this.isBackendReady) {
      this.loadWelcomeMessage();
    }
  }

  observeSection() {
    const chatSection = document.querySelector('#chat');
    if (!chatSection) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          observer.disconnect();
          setTimeout(() => {
            const rect = chatSection.getBoundingClientRect();
            const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
            if (visible / rect.height > 0.5) this.input?.focus();
          }, 500);
        }
      });
    }, { threshold: 0.5 });

    observer.observe(chatSection);
  }

  async _fetchAPI(endpoint, options = {}) {
    const url = `${this.options.apiBaseUrl}${endpoint}`;
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || `HTTP ${response.status}`);
    }
    return data;
  }

  async warmupBackend() {
    this.setChatEnabled(false);
    this.showWarmupOverlay();
    try {
      await this._fetchAPI('/health');
      this.isBackendReady = true;
      this.hideWarmupOverlay();
      this.setChatEnabled(true);
      this.loadWelcomeMessage();
    } catch (error) {
      console.error('Health check failed:', error);
      setTimeout(() => this.warmupBackend(), 3000);
    }
  }

  setTypingMessage(text) {
    if (!this.typing) return;
    const spans = this.typing.querySelectorAll('span');
    const statusSpan = spans[spans.length - 1];
    if (statusSpan) statusSpan.textContent = text;
  }

  setChatEnabled(enabled) {
    if (this.input) this.input.disabled = !enabled;
    if (this.sendButton) this.sendButton.disabled = !enabled;
  }

  showWarmupOverlay() {
    if (!this.typing) return;
    this.typing.innerHTML = '';

    const dots = document.createElement('div');
    dots.className = 'typing-dots';
    dots.append(document.createElement('span'), document.createElement('span'), document.createElement('span'));

    const status = document.createElement('span');
    status.className = 'chat__typing-status';
    status.textContent = 'Connecting to backend...';

    this.typing.append(dots, status);
    this.typing.classList.add('chat__typing--overlay');
    this.typing.removeAttribute('hidden');
    this.typing.style.display = '';
  }

  hideWarmupOverlay() {
    if (!this.typing) return;
    this.typing.classList.remove('chat__typing--overlay');
    if (this.defaultTypingHTML) {
      this.typing.innerHTML = this.defaultTypingHTML;
    }
    this.hideTyping();
  }

  loadWelcomeMessage() {
    if (!this.messages) return;
    const welcome = this.createMessage(
      "Hi! I'm Omar's AI assistant. Ask me anything.",
      'bot',
      { showSuggestions: true }
    );
    this.messages.appendChild(welcome);
  }

  createMessage(content, type = 'user', options = {}) {
    const el = document.createElement('div');
    el.className = `message message--${type}`;
    if (options.isStreaming) el.classList.add('message--streaming');

    if (type === 'bot') {
      const avatar = document.createElement('div');
      avatar.className = 'message__avatar';
      avatar.textContent = '🤖';
      el.appendChild(avatar);
    }

    const contentEl = document.createElement('div');
    contentEl.className = 'message__content';

    const textEl = document.createElement('p');
    textEl.className = 'message__text';
    textEl.textContent = content;
    contentEl.appendChild(textEl);

    if (options.showSuggestions) {
      contentEl.appendChild(this._buildSuggestionChips([
        'Tell me about Omar',
        'What projects has Omar worked on?',
        'How can I contact Omar?',
      ]));
    }

    if (options.timestamp) {
      const ts = document.createElement('div');
      ts.className = 'message__timestamp';
      ts.textContent = options.timestamp;
      contentEl.appendChild(ts);
    }

    el.appendChild(contentEl);
    return el;
  }

  _buildSuggestionChips(texts) {
    const wrapper = document.createElement('div');
    wrapper.className = 'message__suggestions';
    texts.forEach(text => {
      const btn = document.createElement('button');
      btn.className = 'suggestion';
      btn.textContent = text;
      btn.addEventListener('click', () => this.sendMessage(text));
      wrapper.appendChild(btn);
    });
    return wrapper;
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (!this.isBackendReady) {
      this.setTypingMessage('Connecting to backend...');
      this.showTyping();
      return;
    }
    const message = this.input?.value.trim();
    if (!message) return;
    this.sendMessage(message);
    this.input.value = '';
  }

  async sendMessage(message) {
    if (!this.isBackendReady) return;
    this.options.onEvent?.('chat_message_sent', {
      message_length: message.length,
      conversation_id: this.conversationId,
      message_text: message,
    });
    const userMessage = this.createMessage(message, 'user');
    this.messages?.appendChild(userMessage);
    this._streamResponse(message);
  }

  async _streamResponse(message) {
    this.showTyping();
    this.scrollToBottom();

    const botMessageContainer = this.createMessage('', 'bot', { isStreaming: true });
    try {
      await this.sendStreamingMessage(message, botMessageContainer);
    } catch (error) {
      this.hideTyping();
      botMessageContainer.remove();
      this.options.onEvent?.('chat_error', { error: error.message });
      this.addRetryMessage(message);
    }
  }

  async sendStreamingMessage(message, botMessageContainer) {
    const url = `${this.options.apiBaseUrl}/chat/stream`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
      body: JSON.stringify({ message, conversation_id: this.conversationId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';
    let isFirstDelta = true;
    let messageTextElement = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) continue;
          let data;
          try {
            data = JSON.parse(line.substring(6));
          } catch {
            console.warn('Failed to parse SSE data:', line);
            continue;
          }

          switch (data.type) {
            case 'start':
              if (data.conversation_id && data.conversation_id !== 'new') {
                this.conversationId = data.conversation_id;
              }
              break;
            case 'conversation_id':
              if (data.conversation_id) this.conversationId = data.conversation_id;
              break;
            case 'delta':
              if (isFirstDelta) {
                this.messages?.appendChild(botMessageContainer);
                messageTextElement = botMessageContainer.querySelector('.message__text');
                this.hideTyping();
                isFirstDelta = false;
              }
              fullResponse += data.content;
              if (messageTextElement) messageTextElement.textContent = fullResponse;
              this.scrollToBottom();
              break;
            case 'error':
              throw new Error(data.message || 'Streaming error occurred');
            case 'end':
              if (data.conversation_id) this.conversationId = data.conversation_id;
              this.options.onEvent?.('chat_response_received', {
                conversation_id: this.conversationId,
                response_length: fullResponse.length,
              });
              this.finalizeStreamedMessage(botMessageContainer, fullResponse, message);
              return;
          }
        }
      }
      this.finalizeStreamedMessage(botMessageContainer, fullResponse, message);
    } catch (error) {
      this.options.onEvent?.('chat_error', { error: error.message });
      console.error('Streaming error:', error);
      throw error;
    }
  }

  finalizeStreamedMessage(messageContainer, fullResponse, lastUserMessage) {
    if (!messageContainer.parentNode && fullResponse) {
      this.messages?.appendChild(messageContainer);
    }
    messageContainer.classList.remove('message--streaming');
    const textEl = messageContainer.querySelector('.message__text');
    if (textEl) textEl.textContent = fullResponse || 'Response completed.';
    this.hideTyping();
    this.scrollToBottom();
    if (lastUserMessage && fullResponse) {
      this.appendSuggestions(messageContainer, lastUserMessage, fullResponse);
    }
  }

  async appendSuggestions(messageContainer, lastMessage, lastResponse) {
    try {
      const data = await this._fetchAPI('/chat/suggestions', {
        method: 'POST',
        body: JSON.stringify({ last_message: lastMessage, last_response: lastResponse }),
      });
      if (!Array.isArray(data?.suggestions) || !data.suggestions.length) return;
      const content = messageContainer.querySelector('.message__content');
      content?.appendChild(this._buildSuggestionChips(data.suggestions));
      this.scrollToBottom();
    } catch {
      // suggestions are non-critical — silent fail
    }
  }

  addRetryMessage(message) {
    const errorEl = this.createMessage(
      'Connection lost. Your message may not have gone through.',
      'error'
    );
    const content = errorEl.querySelector('.message__content');
    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = 'message__retry';
    retryButton.textContent = 'Retry';
    retryButton.addEventListener('click', () => {
      this.options.onEvent?.('chat_retry_clicked', { conversation_id: this.conversationId });
      errorEl.remove();
      this._streamResponse(message);
    });
    content?.appendChild(retryButton);
    this.messages?.appendChild(errorEl);
    this.scrollToBottom();
  }

  showTyping() {
    if (this.typing) {
      this.typing.removeAttribute('hidden');
      this.typing.style.display = '';
    }
    this.scrollToBottom();
  }

  hideTyping() {
    if (this.typing) {
      this.typing.setAttribute('hidden', '');
      this.typing.style.display = 'none';
    }
  }

  scrollToBottom() {
    if (this.messages) {
      this.messages.scrollTop = this.messages.scrollHeight;
    }
  }
}
